import React, { useState, useEffect } from 'react';
import { Info, CheckCircle } from 'lucide-react';

const BasicInfoForm = ({ testType, formData, onUpdate, onNext }) => {
    const [localData, setLocalData] = useState({
        testName: formData?.basicInfo?.testName || '',
        description: formData?.basicInfo?.description || '',
        goal: formData?.basicInfo?.goal || getDefaultGoal(testType),
    });

    useEffect(() => {
        if (formData?.basicInfo) {
            setLocalData({
                testName: formData.basicInfo.testName || '',
                description: formData.basicInfo.description || '',
                goal: formData.basicInfo.goal || getDefaultGoal(testType),
            });
        }
    }, [formData?.basicInfo]);

    const handleInputChange = (field, value) => {
        setLocalData(prev => ({ ...prev, [field]: value }));
    };

    const handleContinue = () => {
        if (!localData.testName || localData.testName.trim().length === 0) {
            return;
        }

        onUpdate({
            basicInfo: {
                testName: localData.testName.trim(),
                description: localData.description.trim(),
                goal: localData.goal.trim(),
                testType: testType
            }
        });

        onNext();
    };

    const getTestSpecificTips = () => {
        switch (testType) {
            case 'pricing':
                return {
                    title: 'Tips for Pricing Tests',
                    tips: [
                        'Test 2-3 price points to find the optimal balance',
                        'Consider testing price formats: $29.99 vs $30.00',
                        'Run tests for at least 14 days for reliable results'
                    ]
                };
            case 'element':
                return {
                    title: 'Tips for Element Tests',
                    tips: [
                        'Start with button colors and call-to-action text',
                        'Test one element at a time for clear results',
                        'Consider mobile vs desktop differences'
                    ]
                };
            case 'cart':
                return {
                    title: 'Tips for Cart & Discount Tests',
                    tips: [
                        'Test urgency messages and discount displays',
                        'Consider shipping threshold messaging',
                        'Test cart abandonment reduction strategies'
                    ]
                };
            default:
                return {
                    title: 'A/B Testing Tips',
                    tips: [
                        'Keep tests focused on one key change',
                        'Run tests long enough for statistical significance',
                        'Document your hypothesis before starting'
                    ]
                };
        }
    };

    const tips = getTestSpecificTips();
    const isFormValid = localData.testName && localData.testName.trim().length > 0;

    return (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 max-w-4xl mx-auto">
            {/* Section Title */}
            <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900">Test Information</h2>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
                {/* Test Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                        Test Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={localData.testName}
                        onChange={(e) => handleInputChange('testName', e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter a descriptive name for your test"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                        Description
                    </label>
                    <textarea
                        value={localData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                        placeholder="Describe what you're testing and why"
                        rows={3}
                    />
                </div>

                {/* Test Goal */}
                <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                        Test Goal
                    </label>
                    <input
                        type="text"
                        value={localData.goal}
                        onChange={(e) => handleInputChange('goal', e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="What outcome are you trying to achieve?"
                    />
                </div>
            </div>

            {/* Tips Card */}
            <div className="mt-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <div className="flex gap-3">
                    <div className="flex-shrink-0">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                            <Info size={18} className="text-white" />
                        </div>
                    </div>
                    <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">{tips.title}</h4>
                        <ul className="space-y-1.5">
                            {tips.tips.map((tip, index) => (
                                <li key={index} className="flex items-start gap-2 text-xs text-gray-700">
                                    <CheckCircle size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                    <span>{tip}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="mt-5 pt-5 border-t border-gray-200 flex justify-end">
                <button
                    onClick={handleContinue}
                    disabled={!isFormValid}
                    className={`
                        px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200
                        ${isFormValid 
                            ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }
                    `}
                >
                    Continue to Variants
                </button>
            </div>
        </div>
    );
};

function getDefaultGoal(testType) {
    switch (testType) {
        case 'pricing':
            return 'Increase conversion rate by testing different pricing displays';
        case 'element':
            return 'Improve click-through rates by testing button design';
        case 'cart':
            return 'Reduce cart abandonment with better messaging';
        default:
            return '';
    }
}

export default BasicInfoForm;